import React from "react";
import { InlineMath } from "react-katex";

const COLORS = {
  base: "currentColor",
  fill: "rgba(251, 146, 60, 0.13)",
  given: "#f59e0b",
  unknown: "#a855f7",
};

function pointOnCircle(cx, cy, r, deg) {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function anglePath(vertex, p1, p2, r) {
  const a1 = Math.atan2(p1.y - vertex.y, p1.x - vertex.x);
  const a2 = Math.atan2(p2.y - vertex.y, p2.x - vertex.x);
  let delta = a2 - a1;
  while (delta <= -Math.PI) delta += 2 * Math.PI;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  const start = { x: vertex.x + r * Math.cos(a1), y: vertex.y + r * Math.sin(a1) };
  const end = { x: vertex.x + r * Math.cos(a1 + delta), y: vertex.y + r * Math.sin(a1 + delta) };
  return {
    d: `M ${start.x} ${start.y} A ${r} ${r} 0 0 ${delta > 0 ? 1 : 0} ${end.x} ${end.y}`,
    mid: a1 + delta / 2,
  };
}

function Label({ x, y, math, color = COLORS.base, size = 16 }) {
  return (
    <foreignObject x={x - 28} y={y - 14} width="56" height="30">
      <div style={{ color, fontSize: size, lineHeight: 1.2, textAlign: "center" }}>
        <InlineMath math={math} />
      </div>
    </foreignObject>
  );
}

function Point({ p, label, dx = 0, dy = 0 }) {
  return (
    <>
      <circle cx={p.x} cy={p.y} r="4" fill={COLORS.base} />
      <Label x={p.x + dx} y={p.y + dy} math={label} />
    </>
  );
}

function outsideOffset(p, cx, cy, distance = 24) {
  const vx = p.x - cx;
  const vy = p.y - cy;
  const len = Math.hypot(vx, vy) || 1;
  return { dx: (vx / len) * distance, dy: (vy / len) * distance };
}

function PointOutside({ p, label, cx, cy, distance = 24 }) {
  const { dx, dy } = outsideOffset(p, cx, cy, distance);
  return <Point p={p} label={label} dx={dx} dy={dy} />;
}

function Arc({ vertex, p1, p2, value, color, r = 28, labelShift = 16 }) {
  const arc = anglePath(vertex, p1, p2, r);
  const label = {
    x: vertex.x + (r + labelShift) * Math.cos(arc.mid),
    y: vertex.y + (r + labelShift) * Math.sin(arc.mid),
  };
  return (
    <>
      <path d={arc.d} fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" />
      <Label x={label.x} y={label.y} math={value} color={color} size={15} />
    </>
  );
}

function RightAngleMarker({ vertex, p1, p2, size = 13 }) {
  const firstLength = Math.hypot(p1.x - vertex.x, p1.y - vertex.y) || 1;
  const secondLength = Math.hypot(p2.x - vertex.x, p2.y - vertex.y) || 1;
  const first = {
    x: vertex.x + ((p1.x - vertex.x) / firstLength) * size,
    y: vertex.y + ((p1.y - vertex.y) / firstLength) * size,
  };
  const second = {
    x: vertex.x + ((p2.x - vertex.x) / secondLength) * size,
    y: vertex.y + ((p2.y - vertex.y) / secondLength) * size,
  };
  const corner = {
    x: first.x + (second.x - vertex.x),
    y: first.y + (second.y - vertex.y),
  };

  return (
    <path
      d={`M ${first.x} ${first.y} L ${corner.x} ${corner.y} L ${second.x} ${second.y}`}
      fill="none"
      stroke={COLORS.unknown}
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

export default function CirclePolygonAnglesDiagram({
  scenario,
  givenDeg,
  unknownLabel = "\\alpha",
  size = 260,
  showRightAngleAtC = false,
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = Math.round(size * 0.36);

  if (scenario === "semicircle") {
    const A = pointOnCircle(cx, cy, r, 180);
    const B = pointOnCircle(cx, cy, r, 0);
    const O = { x: cx, y: cy };
    const C = pointOnCircle(cx, cy, r, givenDeg?.cDeg ?? 288);
    const givenAtA = givenDeg?.vertex !== "B";
    const givenValue = `${givenDeg?.value ?? ""}^{\\circ}`;
    const unknownVertex = givenAtA ? B : A;
    const unknownP1 = givenAtA ? A : B;
    const unknownP2 = C;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLORS.base} strokeWidth="2" />
        <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`} fill={COLORS.fill} stroke="none" />
        <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={A.x} y1={A.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={B.x} y1={B.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="3" />
        {givenAtA ? (
          <Arc vertex={A} p1={B} p2={C} value={givenValue} color={COLORS.given} r={24} labelShift={13} />
        ) : (
          <Arc vertex={B} p1={C} p2={A} value={givenValue} color={COLORS.given} r={24} labelShift={13} />
        )}
        <Arc vertex={unknownVertex} p1={unknownP1} p2={unknownP2} value={unknownLabel} color={COLORS.unknown} r={31} labelShift={12} />
        {showRightAngleAtC && <RightAngleMarker vertex={C} p1={A} p2={B} />}
        <PointOutside p={A} label="A" cx={cx} cy={cy} distance={24} />
        <Point p={O} label="O" dy={18} />
        <PointOutside p={B} label="B" cx={cx} cy={cy} distance={24} />
        <PointOutside p={C} label="C" cx={cx} cy={cy} distance={22} />
      </svg>
    );
  }

  if (scenario === "radii") {
    const central = Number(givenDeg?.value) || 80;
    const A = pointOnCircle(cx, cy, r, 270 - central / 2);
    const B = pointOnCircle(cx, cy, r, 270 + central / 2);
    const O = { x: cx, y: cy };
    const givenValue = `${givenDeg?.value ?? ""}^{\\circ}`;
    const unknownAtB = givenDeg?.unknownVertex === "B";
    const unknownVertex = unknownAtB ? B : A;
    const unknownP1 = unknownAtB ? A : B;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLORS.base} strokeWidth="2" />
        <polygon points={`${O.x},${O.y} ${A.x},${A.y} ${B.x},${B.y}`} fill={COLORS.fill} stroke="none" />
        <line x1={O.x} y1={O.y} x2={A.x} y2={A.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={O.x} y1={O.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="3" />
        <Arc vertex={O} p1={A} p2={B} value={givenValue} color={COLORS.given} r={28} labelShift={12} />
        <Arc vertex={unknownVertex} p1={O} p2={unknownP1} value={unknownLabel} color={COLORS.unknown} r={25} labelShift={12} />
        <Point p={O} label="O" dy={22} />
        <PointOutside p={A} label="A" cx={cx} cy={cy} distance={26} />
        <PointOutside p={B} label="B" cx={cx} cy={cy} distance={26} />
      </svg>
    );
  }

  if (scenario === "hiddenInscribedTriangle") {
    const given = Number(givenDeg?.value) || 40;
    const A = pointOnCircle(cx, cy, r, 180);
    const B = pointOnCircle(cx, cy, r, 0);
    const O = { x: cx, y: cy };
    const C = pointOnCircle(cx, cy, r, 360 - 2 * given);
    const P = pointOnCircle(cx, cy, r, 135);
    const givenValue = `${given}^{\\circ}`;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLORS.base} strokeWidth="2" />
        <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`} fill={COLORS.fill} stroke="none" />
        <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={A.x} y1={A.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={B.x} y1={B.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="3" />
        <line x1={P.x} y1={P.y} x2={B.x} y2={B.y} stroke={COLORS.given} strokeWidth="2.5" />
        <line x1={P.x} y1={P.y} x2={C.x} y2={C.y} stroke={COLORS.given} strokeWidth="2.5" />
        <Arc vertex={P} p1={B} p2={C} value={givenValue} color={COLORS.given} r={20} labelShift={12} />
        <Arc vertex={B} p1={A} p2={C} value={unknownLabel} color={COLORS.unknown} r={28} labelShift={12} />
        {showRightAngleAtC && <RightAngleMarker vertex={C} p1={A} p2={B} />}
        <PointOutside p={A} label="A" cx={cx} cy={cy} distance={24} />
        <PointOutside p={B} label="B" cx={cx} cy={cy} distance={24} />
        <PointOutside p={C} label="C" cx={cx} cy={cy} distance={24} />
        <PointOutside p={P} label="P" cx={cx} cy={cy} distance={24} />
        <Point p={O} label="O" dy={22} />
      </svg>
    );
  }

  const given = Number(givenDeg?.value) || 80;
  const A = pointOnCircle(cx, cy, r, 225);
  const B = pointOnCircle(cx, cy, r, 10);
  const C = pointOnCircle(cx, cy, r, 330);
  const D = pointOnCircle(cx, cy, r, 10 + 2 * given);
  const givenAtA = true;
  const givenValue = `${givenDeg?.value ?? ""}^{\\circ}`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLORS.base} strokeWidth="2" />
      <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y} ${D.x},${D.y}`} fill={COLORS.fill} stroke="none" />
      {[[A, B], [B, C], [C, D], [D, A]].map(([p, q], i) => (
        <line key={i} x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke={COLORS.base} strokeWidth="3" />
      ))}
      {givenAtA ? (
        <>
          <Arc vertex={A} p1={B} p2={D} value={givenValue} color={COLORS.given} r={25} labelShift={12} />
          <Arc vertex={C} p1={D} p2={B} value={unknownLabel} color={COLORS.unknown} r={25} labelShift={12} />
        </>
      ) : (
        <>
          <Arc vertex={B} p1={C} p2={A} value={givenValue} color={COLORS.given} r={25} labelShift={12} />
          <Arc vertex={D} p1={A} p2={C} value={unknownLabel} color={COLORS.unknown} r={25} labelShift={12} />
        </>
      )}
      <PointOutside p={A} label="A" cx={cx} cy={cy} distance={24} />
      <PointOutside p={B} label="B" cx={cx} cy={cy} distance={24} />
      <PointOutside p={C} label="C" cx={cx} cy={cy} distance={24} />
      <PointOutside p={D} label="D" cx={cx} cy={cy} distance={24} />
    </svg>
  );
}
